/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Progress,
  Spinner,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import OrderCard, { formatISOToShortDate } from '@/components/common/Card/OrderCard';
import paypalImage from '@/assets/png/paypal.png';
import { useGetOrderById } from '@/services/OrderManagement';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import CancelOrderModal from '@/components/Orders/CancelOrderModal';

function formatDateToDayAndDate(inputDate: string): string {
  if (!inputDate) return '';
  const [datePart] = inputDate.split(' ');
  const [month, day, year] = datePart.split('-').map(Number);

  const date = new Date(year, month - 1, day);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  };
  return date.toLocaleDateString('en-US', options);
}

const STATUS_MAPPING: Record<string, string> = {
  ORDER_CONFIRMED: 'Order Confirmed',
  UNDER_MAINTENANCE: 'Under Manufacture',
  READY_FOR_DELIVERY: 'Ready for Delivery',
  SHIPPED: 'Picked Up',
  IN_WAREHOUSE: 'In Warehouse',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURNED: 'Returned',
  DELIVERY_FAILED: 'Delivery Failed',
  RETURN_INITIATED: 'Return Initiated',
  RETURN_ACCEPTED: 'Return Accepted',
  RETURN_REJECTED: 'Return Rejected',
};

const normalizeStatus = (status: string): string => {
  const normalizedKey = status.toUpperCase().replace(/\s/g, '_');
  return STATUS_MAPPING[normalizedKey] || status;
};

const OrderDetails = ({ id }: { id: string }) => {
  const { data: order, isLoading } = useGetOrderById(id);
  const backendStatus = order?.order_status?.status_name;
  const isCustomized = order?.is_customized;
  const normalizedStatus = backendStatus ? normalizeStatus(backendStatus) : '';

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const statusOrder = [
    'Order Confirmed',
    isCustomized ? 'Under Manufacture' : null,
    'Ready for Delivery',
    'Picked Up',
    'In Warehouse',
    'Out for Delivery',
    'Delivered',
    'Delivery Failed',
    'Cancelled',
    'Return Initiated',
    'Return Accepted',
    'Return Rejected',
  ].filter(Boolean);

  const currentStatusIndex = statusOrder.indexOf(normalizedStatus || '');
  let activeStep = currentStatusIndex >= 0 ? currentStatusIndex : 0;

  if (
    ['Returned', 'Return Initiated', 'Return Accepted', 'Return Rejected'].includes(
      normalizedStatus,
    )
  ) {
    if (normalizedStatus === 'Return Initiated')
      activeStep = statusOrder.indexOf('Return Initiated');
    else if (normalizedStatus === 'Return Accepted')
      activeStep = statusOrder.indexOf('Return Accepted');
    else if (normalizedStatus === 'Return Rejected')
      activeStep = statusOrder.indexOf('Return Rejected');
  }

  const baseSteps = [
    {
      title: order?.has_ordered_confirmed
        ? formatDateToDayAndDate(order?.order_confirmed_at ?? '')
        : '',
      description: 'Order Confirmed',
      isActive: activeStep === statusOrder.indexOf('Order Confirmed'),
    },
    ...(isCustomized
      ? [
          {
            title: order?.order_under_maintainece
              ? formatDateToDayAndDate(order?.updated_at ?? '')
              : '',
            description: 'Under Manufacture',
            isActive: activeStep === statusOrder.indexOf('Under Manufacture'),
          },
        ]
      : []),
    {
      title: order?.is_ready_for_delivery
        ? formatDateToDayAndDate(order?.ready_for_delivery_at ?? '')
        : '',
      description: 'Ready for Delivery',
      isActive: activeStep === statusOrder.indexOf('Ready for Delivery'),
    },
    {
      title: order?.has_shipped ? formatDateToDayAndDate(order?.shipped_at ?? '') : '',
      description: 'Picked Up',
      isActive: activeStep === statusOrder.indexOf('Picked Up'),
    },
    {
      title: order?.is_in_warehouse ? formatDateToDayAndDate(order?.in_warehouse_at ?? '') : '',
      description: 'In Warehouse',
      isActive: activeStep === statusOrder.indexOf('In Warehouse'),
    },
    {
      title: order?.has_outfor_delivery
        ? formatDateToDayAndDate(order?.outfor_delivery_at ?? '')
        : '',
      description: 'Out for Delivery',
      isActive: activeStep === statusOrder.indexOf('Out for Delivery'),
    },
    {
      title: order?.has_delivered
        ? formatDateToDayAndDate(order?.delivered_at ?? '')
        : order?.has_delivery_failed
          ? formatDateToDayAndDate(order?.delivery_failed_at ?? '')
          : `Expected by ${formatISOToShortDate(order?.max_estimated_delivery_date ?? '')}`,
      description:
        normalizedStatus === 'Delivered'
          ? 'Delivered'
          : normalizedStatus === 'Delivery Failed'
            ? 'Delivery Failed'
            : 'Expected Delivery',
      isActive:
        activeStep === statusOrder.indexOf('Delivered') ||
        activeStep === statusOrder.indexOf('Delivery Failed'),
    },
  ];

  const terminalSteps = [
    {
      title: order?.has_cancelled ? formatDateToDayAndDate(order?.cancelled_at ?? '') : '',
      description: 'Cancelled',
      isActive: normalizedStatus === 'Cancelled',
    },
    {
      title: order?.has_delivery_failed
        ? formatDateToDayAndDate(order?.delivery_failed_at ?? '')
        : '',
      description: 'Delivery Failed',
      isActive: normalizedStatus === 'Delivery Failed',
    },
  ].filter((step) => step.isActive);

  const returnSteps = [
    {
      title: order?.has_returned ? formatDateToDayAndDate(order?.returned_at ?? '') : '',
      description: 'Return Initiated',
      isActive: ['Return Initiated', 'Returned', 'Return Accepted', 'Return Rejected'].includes(
        normalizedStatus,
      ),
    },
    {
      title: order?.has_returned ? formatDateToDayAndDate(order?.returned_at ?? '') : '',
      description: 'Return Accepted', // Simplified — actual approval not in new structure
      isActive: normalizedStatus === 'Return Accepted',
    },
  ].filter((step) => step.isActive);

  const steps = ['Returned', 'Return Initiated', 'Return Accepted', 'Return Rejected'].includes(
    normalizedStatus,
  )
    ? [...baseSteps, ...returnSteps]
    : [...baseSteps, ...terminalSteps];

  const progressPercent = steps.length > 1 ? (activeStep / (steps.length - 1)) * 100 : 0;

  const isStepComplete = (index: number) => index < activeStep;
  const isStepActive = (index: number) => index === activeStep;

  const transaction = order?.individual_transaction;
  const addOns = order?.customized_fields?.ProductAddOn_ids || [];
  const isAddOn = addOns.length > 0;

  return (
    <>
      <Box px={{ base: 6, xl: 0 }} maxW="container.xl" mx="auto" py={6}>
        <Stack spacing={6}>
          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" h="200px">
              <Spinner />
            </Flex>
          ) : (
            <Grid templateColumns={{ base: '1fr' }} gap={6}>
              <GridItem>
                <OrderCard
                  isDetailPage={true}
                  order={order}
                  onCancelModalOpen={onCancelModalOpen}
                />
              </GridItem>

              {/* Desktop Stepper */}
              <GridItem
                border="1px solid"
                borderColor="border.default"
                borderRadius="lg"
                p={4}
                bg="white"
                display={{ base: 'none', lg: 'block' }}
              >
                <Flex justify={'center'} direction={'column'} align={'center'}>
                  <Flex width={'100%'} justify={'space-between'} mb={6}>
                    {steps.map((step, index) => (
                      <Text
                        key={index}
                        color={
                          isStepComplete(index) || isStepActive(index)
                            ? 'brand.primary.DEFAULT'
                            : 'text.secondary'
                        }
                        fontSize={'xs'}
                        fontFamily={'primary'}
                        fontWeight={'normal'}
                        minWidth={'120px'}
                        align={'center'}
                      >
                        {step.title}
                      </Text>
                    ))}
                  </Flex>

                  <Box width={'90%'} position="relative">
                    <Stepper colorScheme="brand.primary" size="sm" index={activeStep} gap="0">
                      {steps.map((_, index) => (
                        <Step key={index}>
                          <Flex direction={'column'}>
                            <StepIndicator
                              bg="white"
                              zIndex={2}
                              width={'16px'}
                              height={'16px'}
                              border={'2px solid'}
                              borderColor={
                                isStepComplete(index) || isStepActive(index)
                                  ? 'brand.primary.DEFAULT'
                                  : 'gray.200'
                              }
                            >
                              {(isStepComplete(index) || isStepActive(index)) && (
                                <Flex
                                  width={'100%'}
                                  height={'100%'}
                                  rounded={'full'}
                                  justify={'center'}
                                  align={'center'}
                                  bg={'brand.primary.DEFAULT'}
                                />
                              )}
                            </StepIndicator>
                          </Flex>
                        </Step>
                      ))}
                    </Stepper>
                    <Progress
                      colorScheme="brand.primary"
                      value={progressPercent}
                      position="absolute"
                      height="2px"
                      width="full"
                      top="7px"
                      zIndex={1}
                    />
                  </Box>

                  <Flex width={'100%'} justify={'space-between'} mt={6}>
                    {steps.map((step, index) => (
                      <Text
                        key={index}
                        color={
                          isStepComplete(index) || isStepActive(index)
                            ? 'brand.primary.DEFAULT'
                            : 'text.secondary'
                        }
                        fontSize={'xs'}
                        fontFamily={'primary'}
                        fontWeight={'normal'}
                        minWidth={'120px'}
                        align={'center'}
                      >
                        {step.description}
                      </Text>
                    ))}
                  </Flex>
                </Flex>
              </GridItem>

              {/* Mobile Vertical Stepper */}
              <GridItem
                border="1px solid"
                borderColor="border.default"
                borderRadius="lg"
                p={4}
                bg="white"
                display={{ base: 'block', lg: 'none' }}
              >
                <Flex height={'500px'} justifyContent={'center'} p={6}>
                  <Flex width={'fit-content'} gap={6} height={'100%'}>
                    <Flex
                      direction={'column'}
                      height={'100%'}
                      justifyContent={'space-between'}
                      alignItems={'end'}
                    >
                      {steps.map((step, index) => (
                        <Text
                          key={index}
                          color={
                            isStepComplete(index) || isStepActive(index)
                              ? 'brand.primary.DEFAULT'
                              : 'text.secondary'
                          }
                          fontSize={'xs'}
                          fontFamily={'primary'}
                          fontWeight={'normal'}
                        >
                          {step.title}
                        </Text>
                      ))}
                    </Flex>

                    <Flex position={'relative'}>
                      <Stepper
                        colorScheme="brand.primary"
                        size="sm"
                        index={activeStep}
                        gap="0"
                        orientation="vertical"
                      >
                        {steps.map((_, index) => (
                          <Step key={index}>
                            <Flex direction={'row'}>
                              <StepIndicator
                                bg="white"
                                zIndex={2}
                                width={'16px'}
                                height={'16px'}
                                border={'2px solid'}
                                borderColor={
                                  isStepComplete(index) || isStepActive(index)
                                    ? 'brand.primary.DEFAULT'
                                    : 'gray.200'
                                }
                              >
                                {(isStepComplete(index) || isStepActive(index)) && (
                                  <Flex
                                    width={'100%'}
                                    height={'100%'}
                                    rounded={'full'}
                                    justify={'center'}
                                    align={'center'}
                                    bg={'brand.primary.DEFAULT'}
                                  />
                                )}
                              </StepIndicator>
                            </Flex>
                          </Step>
                        ))}
                      </Stepper>
                      <Box
                        position="absolute"
                        height="450px"
                        width="2px"
                        right="7px"
                        bg="gray.200"
                        overflow="hidden"
                      >
                        <Box
                          height={`${progressPercent}%`}
                          bg="brand.primary.DEFAULT"
                          width="100%"
                        />
                      </Box>
                    </Flex>

                    <Flex direction={'column'} height={'100%'} justifyContent={'space-between'}>
                      {steps.map((step, index) => (
                        <Text
                          key={index}
                          color={
                            isStepComplete(index) || isStepActive(index)
                              ? 'brand.primary.DEFAULT'
                              : 'text.secondary'
                          }
                          fontSize={'xs'}
                          fontFamily={'primary'}
                          fontWeight={'normal'}
                        >
                          {step.description}
                        </Text>
                      ))}
                    </Flex>
                  </Flex>
                </Flex>
              </GridItem>

              <GridItem>
                <Grid
                  templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
                  gap={6}
                  fontFamily={'primary'}
                >
                  <GridItem
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="lg"
                    p={4}
                    bg="white"
                    height={'fit-content'}
                  >
                    <Flex direction={'column'} gap={3}>
                      <Text color={'text.primary'} fontSize={'md'} fontWeight={'semibold'}>
                        Shipping Address
                      </Text>
                      <Text color={'text.secondary'} fontSize={'sm'}>
                        {order?.shipping_address?.name ?? ''}
                      </Text>
                      {/* <Text color={'text.secondary'} fontSize={'sm'}>
                        {formatAddress(order?.shipping_address ?? {})}
                      </Text> */}
                    </Flex>
                  </GridItem>

                  <GridItem
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="lg"
                    p={4}
                    bg="white"
                    height={'fit-content'}
                  >
                    <Flex direction={'column'} gap={3}>
                      <Text color={'text.primary'} fontSize={'md'} fontWeight={'semibold'}>
                        Payment Method
                      </Text>
                      <HStack gap={3}>
                        <Image
                          src={paypalImage}
                          alt="paypal"
                          width={'75px'}
                          height={'auto'}
                          loading="lazy"
                        />
                        <Text color={'text.secondary'} fontSize={'sm'}>
                          {order?.payment_method?.method_name || 'PayPal'}
                        </Text>
                      </HStack>
                    </Flex>
                  </GridItem>

                  <GridItem
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="lg"
                    p={4}
                    bg="white"
                    height={'fit-content'}
                  >
                    <Flex direction={'column'} gap={3}>
                      <Text color={'text.primary'} fontSize={'md'} fontWeight={'semibold'}>
                        Order Summary
                      </Text>
                      <Flex direction={'column'} gap={2}>
                        <VStack align={'stretch'} spacing={1}>
                          <HStack justifyContent={'space-between'}>
                            <Text color={'text.secondary'} fontSize={'sm'}>
                              Product Price
                            </Text>
                            <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                              {transaction?.unit_price
                                ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.unit_price).toFixed(2)}`
                                : '-'}
                            </Text>
                          </HStack>

                          {isCustomized && (
                            <HStack justifyContent={'space-between'}>
                              <Text color={'text.secondary'} fontSize={'sm'}>
                                Customization
                              </Text>
                              <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                                {getCurrencySymbol(order?.individual_transaction?.currency)}
                                {Number(transaction?.customization_price || 0).toFixed(2)}
                              </Text>
                            </HStack>
                          )}

                          {isAddOn && (
                            <>
                              <Text color={'text.secondary'} fontSize={'sm'}>
                                Add-ons
                              </Text>
                              {/* Note: add-on details not in new structure; showing total only if needed */}
                              {transaction?.add_on_price > 0 && (
                                <HStack justifyContent={'space-between'} ml={2}>
                                  <Text color={'text.secondary'} fontSize={'sm'}>
                                    Total Add-ons
                                  </Text>
                                  <Text
                                    color={'text.primary'}
                                    fontSize={'sm'}
                                    fontWeight={'semibold'}
                                  >
                                    {getCurrencySymbol(order?.individual_transaction?.currency)}
                                    {Number(transaction.add_on_price).toFixed(2)}
                                  </Text>
                                </HStack>
                              )}
                            </>
                          )}

                          <HStack justifyContent={'space-between'}>
                            <Text color={'text.secondary'} fontSize={'sm'}>
                              Discount
                            </Text>
                            <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                              {transaction?.discount > 0
                                ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.discount).toFixed(2)}`
                                : '-'}
                            </Text>
                          </HStack>

                          <HStack justifyContent={'space-between'}>
                            <Text color={'text.secondary'} fontSize={'sm'}>
                              Shipping
                            </Text>
                            <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                              {transaction?.shipping_cost
                                ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.shipping_cost).toFixed(2)}`
                                : 'Free'}
                            </Text>
                          </HStack>

                          <HStack justifyContent={'space-between'}>
                            <Text color={'text.secondary'} fontSize={'sm'}>
                              Tax
                            </Text>
                            <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                              {transaction?.tax_amount
                                ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.tax_amount).toFixed(2)}`
                                : '0.00'}
                            </Text>
                          </HStack>
                        </VStack>

                        <Divider borderColor={'border.default'} />

                        <HStack justifyContent={'space-between'}>
                          <Text fontWeight={'semibold'} color={'text.primary'} fontSize={'sm'}>
                            Grand Total
                          </Text>
                          <Text color={'text.primary'} fontSize={'sm'} fontWeight={'semibold'}>
                            {transaction?.grand_total
                              ? `${getCurrencySymbol(order?.individual_transaction?.currency)}${Number(transaction.grand_total).toFixed(2)}`
                              : '-'}
                          </Text>
                        </HStack>
                      </Flex>
                    </Flex>
                  </GridItem>
                </Grid>
              </GridItem>
            </Grid>
          )}
        </Stack>
      </Box>

      {order && (
        <CancelOrderModal
          isOpen={isCancelModalOpen}
          onClose={onCancelModalClose}
          orderId={order.order_id ?? ''}
          amount={transaction?.grand_total?.toString() ?? ''}
          product={{ name: order.product } as any}
          variantImages={order.image ?? []}
        />
      )}
    </>
  );
};

export default OrderDetails;
